import { useCallback, useEffect, useMemo, useState } from "react";

import { envConfig } from "~/lib/env-validation";
import { useAuth } from "~/lib/hooks/use-auth";

export type OnboardingStep = "welcome" | "profile" | "use-case" | "first-search" | "complete";

interface OnboardingUser {
	name: string | null;
	onboardingStep: OnboardingStep;
	onboardingUseCase: string | null;
}

export function useOnboarding() {
	const { session } = useAuth();
	const [data, setData] = useState<OnboardingUser | null>(null);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		if (!session?.access_token) {
			setLoading(false);
			return;
		}

		fetch(`${envConfig.apiBaseUrl}/auth/me`, {
			headers: { Authorization: `Bearer ${session.access_token}` },
		})
			.then((res) => res.json())
			.then((user) => {
				setData({
					name: user.name ?? null,
					onboardingStep: user.onboardingStep ?? "complete",
					onboardingUseCase: user.onboardingUseCase ?? null,
				});
			})
			.catch(() => {
				// On error, skip onboarding — don't block the user
				setData({ name: null, onboardingStep: "complete", onboardingUseCase: null });
			})
			.finally(() => setLoading(false));
	}, [session?.access_token]);

	const updateStep = useCallback(
		async (payload: { step?: string; useCase?: string; name?: string; completed?: boolean }) => {
			if (!session?.access_token) return;
			const res = await fetch(`${envConfig.apiBaseUrl}/auth/onboarding`, {
				method: "PATCH",
				headers: {
					"Content-Type": "application/json",
					Authorization: `Bearer ${session.access_token}`,
				},
				body: JSON.stringify(payload),
			});
			if (!res.ok) throw new Error("Onboarding update failed");
			const updated = await res.json();
			setData({
				name: updated.name ?? null,
				onboardingStep: updated.onboardingStep ?? "complete",
				onboardingUseCase: updated.onboardingUseCase ?? null,
			});
		},
		[session?.access_token],
	);

	const isOnboardingComplete = useMemo(() => !data || data.onboardingStep === "complete", [data]);

	return { data, loading, updateStep, isOnboardingComplete };
}
